import { HubSearchResult } from '../../types/hubSearch';
import { CRMUser, OpsComment, OpsTask } from '../../types';
import { opsOriginLabel } from '../opsOriginDisplay';
import { buildSearchableText, scoreMultiFieldMatch } from '../search';
import { getActionRequiredShortLabel } from '../actionRequiredDisplay';

export interface SearchOpsHubOptions {
  query: string;
  opsTasks: OpsTask[];
  crmUsers: CRMUser[];
  opsComments?: OpsComment[];
  limit?: number;
}

const SECTION_ORDER: Record<string, number> = {
  'Ops tasks': 0,
  'Ops comments': 1,
};

function userName(uid: string | undefined, crmUsers: CRMUser[]): string {
  if (!uid) return '';
  return crmUsers.find(u => u.uid === uid)?.displayName || '';
}

function sortResults(results: HubSearchResult[]): HubSearchResult[] {
  return [...results].sort((a, b) => {
    if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
    return (SECTION_ORDER[a.section] ?? 99) - (SECTION_ORDER[b.section] ?? 99);
  });
}

export function searchOpsHub(options: SearchOpsHubOptions): HubSearchResult[] {
  const { query, opsTasks, crmUsers, opsComments = [], limit = 30 } = options;
  const q = query.trim();

  if (!q) {
    return opsTasks
      .filter(t => t.id && t.status !== 'completed')
      .slice(0, 12)
      .map(task => ({
        id: task.id!,
        kind: 'opsTask' as const,
        section: 'Ops tasks',
        title: task.title,
        subtitle: [task.category, task.status, task.priority].filter(Boolean).join(' · '),
        matchScore: 1,
        payload: task,
      }));
  }

  const taskById = new Map(opsTasks.filter(t => t.id).map(t => [t.id!, t]));
  const results: HubSearchResult[] = [];

  for (const task of opsTasks) {
    if (!task.id) continue;
    const assigneeNames = (task.assignedTo || [])
      .concat(task.collaborators || [])
      .map(uid => userName(uid, crmUsers))
      .join(' ');
    const subtaskTitles = (task.subtasks || []).map(s => s.title).join(' ');
    const actionLabel = task.actionRequired
      ? getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, crmUsers)
      : '';
    const origin = opsOriginLabel(task.origin);

    const score = scoreMultiFieldMatch(q, [
      { text: task.title, weight: 10 },
      { text: task.description, weight: 7 },
      { text: (task.tags || []).join(' '), weight: 6 },
      { text: task.category, weight: 4 },
      { text: task.status, weight: 3 },
      { text: task.priority, weight: 3 },
      { text: assigneeNames, weight: 5 },
      { text: actionLabel, weight: 5 },
      { text: task.actionRequiredNote, weight: 5 },
      { text: subtaskTitles, weight: 4 },
      { text: origin, weight: 4 },
      { text: task.requestedBy, weight: 3 },
    ]);

    if (score <= 0) continue;
    results.push({
      id: task.id,
      kind: 'opsTask',
      section: 'Ops tasks',
      title: task.title,
      subtitle: [task.category, assigneeNames, actionLabel].filter(Boolean).join(' · '),
      matchScore: score,
      payload: task,
    });
  }

  for (const comment of opsComments) {
    if (!comment.id) continue;
    const task = taskById.get(comment.taskId);
    const body = buildSearchableText(comment.bodyHtml || comment.text);
    const score = scoreMultiFieldMatch(q, [
      { text: body, weight: 8 },
      { text: comment.userName, weight: 4 },
      { text: task?.title, weight: 5 },
    ]);
    if (score <= 0) continue;
    results.push({
      id: comment.id,
      kind: 'opsComment',
      section: 'Ops comments',
      title: task?.title ? `Comment on ${task.title}` : 'Ops comment',
      subtitle: [comment.userName, body.slice(0, 80)].filter(Boolean).join(' · '),
      matchScore: score,
      payload: { comment, task },
    });
  }

  return sortResults(results).slice(0, limit);
}
